import { roundHalfDown } from "../../helpers/currency"

// Formateo amigable del campo `scales` para la bitácora y el comparador de
// versiones. El valor de `scales` llega en dos formatos distintos según el
// origen:
//   - Bitácora (change log): el backend hace str(list[dict]) -> repr de Python
//     con comillas simples y None/True/False, p.ej.
//     "[{'external_id': '1', 'order': 0, 'origin': 'A', 'destination': 'B',
//        'kms': 10.5, 'duration': 900, 'amount': None, 'customer_amount': None}]"
//   - Diff (comparador): llega como array/objeto JSON real.
// Este helper normaliza ambos a un array de objetos y produce una vista legible.

// Intenta convertir un repr de Python a JSON válido para poder parsearlo.
// Es un best-effort acotado a los valores que produce _scale_snapshot
// (strings, números, None/True/False). No pretende ser un parser general.
const pythonReprToJson = (s) =>
  s
    // None/True/False -> null/true/false (con límites de palabra)
    .replace(/\bNone\b/g, "null")
    .replace(/\bTrue\b/g, "true")
    .replace(/\bFalse\b/g, "false")
    // comillas simples -> dobles (claves y valores string).
    // Nota: asume que los textos no contienen comillas simples internas; si las
    // hubiera, el parseo falla y se cae al fallback (mostrar el string tal cual).
    .replace(/'/g, '"')

// Convierte el valor crudo de `scales` a un array de objetos, o null si no se
// puede interpretar. Acepta: array ya parseado, JSON string, o repr de Python.
export const parseScales = (raw) => {
  if (raw == null || raw === "") return []
  if (Array.isArray(raw)) return raw
  if (typeof raw === "object") return [raw]
  if (typeof raw !== "string") return null

  const str = raw.trim()
  if (str === "[]") return []

  // 1) JSON directo.
  try {
    const parsed = JSON.parse(str)
    return Array.isArray(parsed) ? parsed : [parsed]
  } catch (_e) { /* sigue */ }

  // 2) repr de Python -> JSON.
  try {
    const parsed = JSON.parse(pythonReprToJson(str))
    return Array.isArray(parsed) ? parsed : [parsed]
  } catch (_e) { /* no interpretable */ }

  return null
}

const dash = (v) => (v === null || v === undefined || v === "" ? "—" : v)

// Descripción compacta de una escala en una sola línea.
// Ej.: "1. Origen → Destino · 10.5 km · 15 min · prov $0 · cliente $0"
export const scaleLine = (sc, idx) => {
  if (!sc || typeof sc !== "object") return String(sc)
  const parts = []
  const pos = sc.order != null ? sc.order + 1 : idx != null ? idx + 1 : null
  const head = `${dash(sc.origin)} → ${dash(sc.destination)}`
  parts.push(pos != null ? `${pos}. ${head}` : head)
  const meta = []
  if (sc.kms != null && sc.kms !== "") meta.push(`${sc.kms} km`)
  if (sc.duration != null && sc.duration !== "") {
    const mins = Math.round(Number(sc.duration) / 60)
    meta.push(Number.isFinite(mins) ? `${mins} min` : `${sc.duration}`)
  }
  // Montos ajustados a .00/.50 (regla SV2), coherente con fmtMXN.
  if (sc.amount != null && sc.amount !== "" && Number.isFinite(Number(sc.amount))) {
    meta.push(`prov $${roundHalfDown(Number(sc.amount)).toFixed(2)}`)
  }
  if (sc.customer_amount != null && sc.customer_amount !== "" && Number.isFinite(Number(sc.customer_amount))) {
    meta.push(`cliente $${roundHalfDown(Number(sc.customer_amount)).toFixed(2)}`)
  }
  return meta.length ? `${parts[0]} · ${meta.join(" · ")}` : parts[0]
}

// ¿El nombre de campo corresponde a escalas?
export const isScalesField = (field) => field === "scales"
